// The Index Sheet code: <ISA>-<MFR>-<GRP>-<INST>[-<SUFFIX>], e.g. CV-BRY-005-001-FL. ISA is the ISA
// type (1-5 letters), MFR the manufacturer Index_Code (2-4 letters or digits), GRP and INST are
// exactly three digits, SUFFIX is optionally FL, FO or FC. Codes are compared trimmed and
// case-insensitively; parsed parts come back uppercase.
//
// A code is a placeholder (blank, N/A, Integral To ..., See Panels, TBD), conforming (it parses), or
// non-conforming (a real code that is off-grammar: other suffixes, two-digit instances, underscores
// or spaces). Placeholders are hidden everywhere; non-conforming codes cannot be promoted until
// renamed.
//
// The next free instance for a prefix (ISA-MFR-GRP) is the highest instance in use plus one,
// ignoring suffix, so FL / FO / FC variants of one device share one number.
//
// Catalog status sorts Preferred, Active (or blank), Do Not Use, Obsolete, Unreviewed, anything
// else. Only Do Not Use is hidden by default.
//
// The group number for a valve is its size in inches times ten, truncated, from the size text.
// Fractions are exact (1-1/4" is 012, not 012.4999). Anything unparseable returns null: enter it by
// hand.
//
// Mirrored on the server by PMD-API/PMD.API/Services/IndexSheetCode.cs. Change both.

export const MAX_INSTANCE = 999;
export const KIND = { PLACEHOLDER: 'placeholder', CONFORMING: 'conforming', NONCONFORMING: 'nonconforming' };

const SUFFIXES = ['FL', 'FO', 'FC'];
const PATTERN = /^([A-Z]{1,5})-([A-Z0-9]{2,4})-([0-9]{3})-([0-9]{3})(?:-(FL|FO|FC))?$/i;
const PREFIX_PATTERN = /^([A-Z]{1,5})-([A-Z0-9]{2,4})-([0-9]{3})$/i;

// { isa, mfr, group, instance, suffix, prefix } (uppercased; suffix '' when absent) or null.
export function parseIndexSheetCode(code) {
  const m = PATTERN.exec(String(code ?? '').trim());
  if (!m) return null;
  const [isa, mfr, group, instance] = [m[1].toUpperCase(), m[2].toUpperCase(), m[3], m[4]];
  return { isa, mfr, group, instance, suffix: (m[5] || '').toUpperCase(), prefix: `${isa}-${mfr}-${group}` };
}

export function isPlaceholder(code) {
  const c = String(code ?? '').trim().toLowerCase();
  return c === '' || c === 'n/a' || c === 'see panels' || c === 'tbd' || c.startsWith('integral to');
}

export function classifyIndexSheetCode(code) {
  if (isPlaceholder(code)) return KIND.PLACEHOLDER;
  return parseIndexSheetCode(code) ? KIND.CONFORMING : KIND.NONCONFORMING;
}

const pad3 = (v, what) => {
  const t = String(v ?? '').trim();
  if (!/^[0-9]{1,3}$/.test(t)) throw new Error(`${what} '${v}' must be 1-3 digits.`);
  return t.padStart(3, '0');
};

// Group and instance may be numbers or digit strings (e.g. the output of sizeToGroup). Throws on an
// invalid part.
export function buildIndexSheetCode(isa, mfr, group, instance, suffix) {
  const sfx = suffix && String(suffix).trim() ? String(suffix).trim().toUpperCase() : '';
  if (sfx && !SUFFIXES.includes(sfx)) throw new Error(`Suffix '${suffix}' must be FL, FO or FC.`);
  const code = `${String(isa ?? '').trim()}-${String(mfr ?? '').trim()}-${pad3(group, 'Group')}-${pad3(instance, 'Instance')}${sfx ? '-' + sfx : ''}`;
  if (!parseIndexSheetCode(code)) {
    throw new Error(`'${code}' is not a valid Index Sheet code (ISA 1-5 letters, MFR 2-4 letters or digits).`);
  }
  return code.toUpperCase();
}

// The next `count` free instances (three-digit strings) for a prefix ISA-MFR-GRP: highest instance
// among existingCodes sharing the prefix, plus one onward. Throws when the run would pass 999.
export function nextInstance(prefix, existingCodes, count = 1) {
  if (!Number.isInteger(count) || count < 1) throw new Error('Count must be at least 1.');
  const want = String(prefix ?? '').trim().toUpperCase();
  if (!PREFIX_PATTERN.test(want)) throw new Error(`'${prefix}' is not a prefix (ISA-MFR-GRP).`);
  let max = 0;
  for (const c of existingCodes || []) {
    const parts = parseIndexSheetCode(c);
    if (parts && parts.prefix === want) max = Math.max(max, Number(parts.instance));
  }
  if (max + count > MAX_INSTANCE) {
    throw new Error(`${want} has no room for ${count} more instance(s); the last is ${MAX_INSTANCE}.`);
  }
  return Array.from({ length: count }, (_, i) => String(max + 1 + i).padStart(3, '0'));
}

// ---- Catalog status ----

// Preferred 0, Active or blank 1, Do Not Use 2, Obsolete 3, Unreviewed 4, anything else 5.
const RANKS = { preferred: 0, '': 1, active: 1, 'do not use': 2, obsolete: 3, unreviewed: 4 };
export const statusRank = status => RANKS[String(status ?? '').trim().toLowerCase()] ?? 5;
export const compareStatus = (a, b) => statusRank(a) - statusRank(b);

// Do Not Use is hidden from the pickers unless "show all" is ticked. Obsolete is not.
export const isHiddenByDefault = status => String(status ?? '').trim().toLowerCase() === 'do not use';

// ---- Size to group ----

const VULGAR = {
  '\u00BD': [1, 2], '\u00BC': [1, 4], '\u00BE': [3, 4],
  '\u215B': [1, 8], '\u215C': [3, 8], '\u215D': [5, 8], '\u215E': [7, 8],
};
// Optional unit after a size: inch mark (straight or curly), two apostrophes, in, inch, inches.
const UNIT = '\\s*(?:"|\u2033|\u201D|\'\'|in\\.?|inch|inches)?\\s*';
const DECIMAL_SIZE = new RegExp(`^([0-9]*)\\.([0-9]+)${UNIT}$`, 'i');
const WHOLE_SIZE = new RegExp(`^([0-9]+)${UNIT}$`, 'i');
const FRACTION_SIZE = new RegExp(`^(?:([0-9]+)(?:\\s+|\\s*-\\s*))?([0-9]+)\\s*/\\s*([0-9]+)${UNIT}$`, 'i');
const VULGAR_SIZE = new RegExp(`^([0-9]+)?\\s*-?\\s*([\u00BD\u00BC\u00BE\u215B\u215C\u215D\u215E])${UNIT}$`, 'i');

// A size as an exact fraction of an inch, { num, den }, or null when the text is not a size (N/A,
// DN50, blank, words). Accepts 1/2", 1/2, 0.5", 1-1/4", 1 1/4", 1.25", 2", 2 in and the vulgar
// fractions.
export function parseSizeInches(size) {
  const s = String(size ?? '').trim();
  if (!s) return null;
  let m;
  if ((m = DECIMAL_SIZE.exec(s))) {
    if (m[2].length > 9) return null;
    const den = 10 ** m[2].length;
    return { num: (m[1] ? Number(m[1]) : 0) * den + Number(m[2]), den };
  }
  if ((m = WHOLE_SIZE.exec(s))) return { num: Number(m[1]), den: 1 };
  if ((m = FRACTION_SIZE.exec(s))) {
    const den = Number(m[3]);
    if (den === 0) return null;
    return { num: (m[1] ? Number(m[1]) : 0) * den + Number(m[2]), den };
  }
  if ((m = VULGAR_SIZE.exec(s))) {
    const [n, d] = VULGAR[m[2]];
    return { num: (m[1] ? Number(m[1]) : 0) * d + n, den: d };
  }
  return null;
}

// Valve group from size text: floor(inches x 10) as three digits (1/2" 005, 1-1/4" 012, 2" 020,
// 10" 100). Null when the text is not a size or the result is outside 001-999: the group is entered
// by hand.
export function sizeToGroup(size) {
  const r = parseSizeInches(size);
  if (!r) return null;
  const g = Math.floor((r.num * 10) / r.den);
  return g < 1 || g > 999 ? null : String(g).padStart(3, '0');
}
